import { useEffect } from 'react';
import gql from 'graphql-tag';
import { useSubscription } from '@apollo/react-hooks';

import { useSubscriptionValues } from '@confluence/graphql/entry-points/useSubscriptionValues';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { setContentErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { getStatusCode } from '@confluence/error-boundary/entry-points/isStatusCodeError';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery';

export const errorStringsToIgnore = [
	'401',
	'Failed to fetch',
	'Load Failed',
	'Network error',
	'GraphQL error: undefined',
];

export const onContentTypesTitleSubscriptionData = (
	response: any,
	onDataReceived: (contentState: any) => void,
) => {
	const payload = response?.subscriptionData?.data?.confluence_onContentModified;
	const _deltas = payload?._deltas;
	if (!payload || !Array.isArray(_deltas)) {
		return;
	}
	if (_deltas?.length === 0 || _deltas.indexOf('contentTitleUpdated') === -1) {
		return;
	}
	const { contentTitleUpdated } = payload;

	const title = contentTitleUpdated?.contentTitle;
	onDataReceived(title);
};

export const ContentTypesTitleSubscription = ({
	contentType,
	contentId,
	onDataReceived,
	skip = false,
}: {
	contentType: 'page' | 'blogpost';
	contentId: string;
	onDataReceived: (title: any) => void;
	skip?: boolean;
}) => {
	const { id, onSubscriptionComplete, extensions, onSubscriptionData } = useSubscriptionValues({
		contentType,
		contentId,
	});
	const { accessStatus } = useSessionData();
	const skipSubscriptionForAnonUsers = accessStatus === AccessStatus.ANONYMOUS_ACCESS;

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const { error } = useSubscription(
		gql`
			subscription ContentTypesTitleSubscription($id: ID!) {
				confluence_onContentModified(id: $id) {
					type
					id
					__typename
					_deltas
					contentTitleUpdated {
						contentTitle
						__typename
					}
				}
			}
		`,
		{
			variables: {
				id,
				__extensions: extensions,
			},
			onSubscriptionComplete,
			onSubscriptionData(response) {
				onContentTypesTitleSubscriptionData(response, onDataReceived);
				onSubscriptionData(response);
			},
			skip: skip || !id || skipSubscriptionForAnonUsers,
			client: getAGGClient(),
		},
	);

	useEffect(() => {
		if (error) {
			// if there is no error message it is likely to be a client side network issue
			// eg spotty vpn
			const errorMessage =
				error?.message ||
				(error as any)?.errors?.[0]?.message ||
				error?.graphQLErrors?.[0]?.message ||
				error?.graphQLErrors?.[0]?.originalError?.message ||
				error?.graphQLErrors?.[0]?.extensions?.data?.errors?.[0]?.message ||
				error?.graphQLErrors?.[0]?.extensions?.message;

			const isIgnoredError =
				errorStringsToIgnore.some((errorString) => error?.message.includes(errorString)) ||
				!errorMessage;
			const statusCode =
				getStatusCode(error) || (error as any)?.errors?.[0]?.extensions?.statusCode;
			if (statusCode !== 403 && !isIgnoredError) {
				const e = new Error(`Subscription Error - ContentTypesTitleSubscription:${errorMessage}`);
				setContentErrorAttributes(e, {
					objectId: contentId,
					objectType: contentType,
				});
				getMonitoringClient().submitError(e, {
					attribution: Attribution.PAGE_EXPERIENCES,
				});
			}
			markErrorAsHandled(error);
		}
	}, [error, contentId, contentType]);

	return null;
};
